/**
 * Test Scenario Manager
 *
 * Manages test user scenarios for development and testing.
 * Scenarios are stored in sessionStorage and used to select
 * appropriate mock API responses via Prism's example selection.
 */

// Use standard browser detection instead of SvelteKit's $app/environment
// to avoid build issues during E2E test execution
const browser = typeof window !== 'undefined';

export type ScenarioCategory = 'access' | 'status' | 'ocr' | 'data';

export interface TestScenario {
	id: string;
	name: string;
	email: string;
	description: string;
	expectedRoute: string;
	category: ScenarioCategory;
}

/**
 * Available test scenarios
 * Each scenario maps to a named example in the OpenAPI overlay
 */
export const TEST_SCENARIOS: TestScenario[] = [
	// Access Control Scenarios
	{
		id: 'canSubmit',
		name: 'Can Submit',
		email: 'cansubmit@test.com',
		description: 'Happy path - user can submit new meter reading',
		expectedRoute: 'StartSubmission screen',
		category: 'access'
	},
	{
		id: 'blocked',
		name: 'Blocked',
		email: 'blocked@test.com',
		description: 'User cannot access mobile app (multi-facility)',
		expectedRoute: 'BlockedScreen',
		category: 'access'
	},
	{
		id: 'submitted',
		name: 'Already Submitted',
		email: 'submitted@test.com',
		description: 'User already submitted reading for current month',
		expectedRoute: 'Dashboard',
		category: 'access'
	},
	{
		id: 'outside',
		name: 'Outside Window',
		email: 'outside@test.com',
		description: 'Not submission time (days 16-31)',
		expectedRoute: 'Dashboard',
		category: 'access'
	},

	// Status Card Scenarios
	{
		id: 'status_approved',
		name: 'Approved',
		email: 'status_approved@test.com',
		description: 'Reading Verified status card',
		expectedRoute: 'Dashboard + green card',
		category: 'status'
	},
	{
		id: 'status_pending',
		name: 'Pending Review',
		email: 'status_pending@test.com',
		description: 'Reading Submitted & Awaiting Review card',
		expectedRoute: 'Dashboard + blue card',
		category: 'status'
	},
	{
		id: 'status_high',
		name: 'High Reading',
		email: 'status_high@test.com',
		description: 'High Reading Detected warning card (999,345 kWh)',
		expectedRoute: 'Dashboard + orange card',
		category: 'status'
	},
	{
		id: 'status_low',
		name: 'Low Reading',
		email: 'status_low@test.com',
		description: 'Low Reading Detected warning card (12 kWh)',
		expectedRoute: 'Dashboard + orange card',
		category: 'status'
	},
	{
		id: 'status_less_than_prev',
		name: 'Less Than Prev',
		email: 'status_less@test.com',
		description: 'Reading Lower Than Last Month warning card',
		expectedRoute: 'Dashboard + orange card',
		category: 'status'
	},
	{
		id: 'status_disapproved',
		name: 'Disapproved',
		email: 'status_disapproved@test.com',
		description: 'Reading Rejected error card with retry',
		expectedRoute: 'Dashboard + red card',
		category: 'status'
	},
	{
		id: 'status_invalid_meter',
		name: 'Invalid Meter',
		email: 'status_invalid@test.com',
		description: 'Invalid Meter Type warning card',
		expectedRoute: 'Dashboard + orange card',
		category: 'status'
	},
	{
		id: 'status_welcome',
		name: 'Welcome (New)',
		email: 'status_welcome@test.com',
		description: 'Welcome aboard! card for new users',
		expectedRoute: 'Dashboard + welcome card',
		category: 'status'
	},

	// OCR Flow Scenarios
	{
		id: 'ocr_success',
		name: 'OCR Success',
		email: 'ocr_success@test.com',
		description: 'OCR processes image successfully',
		expectedRoute: 'Review screen with extracted reading',
		category: 'ocr'
	},
	{
		id: 'ocr_pending',
		name: 'OCR Pending',
		email: 'ocr_pending@test.com',
		description: 'OCR queued but not started',
		expectedRoute: 'Processing spinner',
		category: 'ocr'
	},
	{
		id: 'ocr_processing',
		name: 'OCR Processing',
		email: 'ocr_processing@test.com',
		description: 'OCR currently processing image',
		expectedRoute: 'Processing spinner',
		category: 'ocr'
	},
	{
		id: 'ocr_failed',
		name: 'OCR Failed',
		email: 'ocr_failed@test.com',
		description: 'OCR failed - manual entry required',
		expectedRoute: 'Manual entry prompt',
		category: 'ocr'
	},

	// Auth Scenarios
	{
		id: 'auth_401',
		name: '401 on Submit',
		email: 'expired@test.com',
		description: 'Login works, but manual entry submit returns 401 (test refresh)',
		expectedRoute: 'Manual entry → 401 → refresh attempt',
		category: 'access'
	},

	// Data Variant Scenarios
	{
		id: 'user_no_facilities',
		name: 'No Facilities',
		email: 'no_facilities@test.com',
		description: 'User has no facilities associated',
		expectedRoute: 'BlockedScreen',
		category: 'data'
	},
	{
		id: 'history_partial',
		name: 'Partial History',
		email: 'partial_history@test.com',
		description: 'Newer user with 3 months of history',
		expectedRoute: 'Dashboard (shorter chart)',
		category: 'data'
	},
	{
		id: 'transactions_empty',
		name: 'No Transactions',
		email: 'no_transactions@test.com',
		description: 'User with no payment history yet',
		expectedRoute: 'Dashboard (empty earnings)',
		category: 'data'
	}
];

/**
 * Get scenarios by category
 */
export function getScenariosByCategory(category: ScenarioCategory): TestScenario[] {
	return TEST_SCENARIOS.filter((s) => s.category === category);
}

/**
 * Category display names and colors
 */
export const CATEGORY_INFO: Record<ScenarioCategory, { name: string; color: string }> = {
	access: { name: 'Access Control', color: 'blue' },
	status: { name: 'Status Cards', color: 'purple' },
	ocr: { name: 'OCR Flow', color: 'green' },
	data: { name: 'Data Variants', color: 'orange' }
};

const SCENARIO_STORAGE_KEY = 'mock_scenario';

/**
 * Set the active test scenario
 * Stores scenario ID in sessionStorage for API client to read
 */
export function setScenario(scenarioId: string): void {
	if (!browser) return;

	const scenario = TEST_SCENARIOS.find((s) => s.id === scenarioId);
	if (!scenario) {
		console.warn(`Unknown scenario ID: ${scenarioId}`);
		return;
	}

	sessionStorage.setItem(SCENARIO_STORAGE_KEY, scenarioId);
	console.log(`[Scenarios] Active scenario: ${scenario.name} (${scenarioId})`);
}

/**
 * Get the currently active scenario ID
 * Returns null if no scenario is set
 */
export function getScenario(): string | null {
	if (!browser) return null;
	const scenario = sessionStorage.getItem(SCENARIO_STORAGE_KEY);
	console.log('[Scenarios] Getting scenario:', scenario);
	return scenario;
}

/**
 * Get the full scenario object for the current scenario
 */
export function getCurrentScenario(): TestScenario | null {
	const scenarioId = getScenario();
	if (!scenarioId) return null;
	return TEST_SCENARIOS.find((s) => s.id === scenarioId) || null;
}

/**
 * Clear the active scenario
 */
export function clearScenario(): void {
	if (!browser) return;
	sessionStorage.removeItem(SCENARIO_STORAGE_KEY);
	console.log('[Scenarios] Scenario cleared');
}

/**
 * Get scenario by ID
 */
export function getScenarioById(scenarioId: string): TestScenario | undefined {
	return TEST_SCENARIOS.find((s) => s.id === scenarioId);
}
